import { useState } from "react";
import api from "@/lib/api";
import { useConfig } from "@/hooks/useConfig";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search } from "lucide-react";

export default function DPPincodeSearchPage() {
  const [pincode, setPincode] = useState("");
  const [state, setState] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const { getItems } = useConfig();
  const states = (getItems("indian_states") || []).map(s => s.value || s.key);

  const doSearch = async () => {
    if (!pincode.trim()) return;
    setLoading(true);
    try {
      const r = await api.get(`/dp/territory/pincode/${pincode.trim()}${state ? `?state=${encodeURIComponent(state)}` : ""}`);
      setResult(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Search failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="dp-pincode-search-page">
      <h1 className="text-xl font-semibold text-zinc-900 mb-1">Pincode Availability</h1>
      <p className="text-xs text-zinc-400 mb-4">Check which brands are taken or free for a pincode</p>
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
          <Input placeholder="Enter pincode (e.g. 500081)" className="pl-7 h-9 text-sm" value={pincode} onChange={e => setPincode(e.target.value)} onKeyDown={e => e.key === "Enter" && doSearch()} data-testid="pincode-input" />
        </div>
        <select className="h-9 text-sm border rounded-md px-2 max-w-[180px]" value={state} onChange={e => setState(e.target.value)} data-testid="state-select">
          <option value="">State (optional)</option>
          {states.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <Button size="sm" onClick={doSearch} disabled={loading} data-testid="search-btn">{loading ? "Searching..." : "Search"}</Button>
      </div>
      {result && (
        <div data-testid="search-results">
          <h2 className="text-sm font-medium text-zinc-700 mb-2">Pincode: <span className="font-mono">{result.pincode}</span>{result.state ? <span className="ml-2 text-zinc-500">State: {result.state}</span> : null}</h2>
          <div className="border border-zinc-200 rounded-sm overflow-hidden">
            <table className="w-full text-xs">
              <thead><tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="text-left px-3 py-2 font-medium text-zinc-600">Brand</th>
                <th className="text-left px-3 py-2 font-medium text-zinc-600">Status</th>
                <th className="text-left px-3 py-2 font-medium text-zinc-600">Held By</th>
                <th className="text-left px-3 py-2 font-medium text-zinc-600">Notes</th>
              </tr></thead>
              <tbody>
                {result.brands.map(b => (
                  <tr key={b.brand_id} className="border-b border-zinc-100" data-testid={`brand-row-${b.brand_id}`}>
                    <td className="px-3 py-2 font-medium text-zinc-800">{b.brand_name}</td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className={`text-[10px] ${b.state === "taken" ? "bg-red-50 text-red-600 border-red-200" : "bg-emerald-50 text-emerald-600 border-emerald-200"}`}>{b.state}</Badge>
                      {b.restricted && <Badge variant="outline" className="ml-1 text-[10px] bg-red-600 text-white border-red-700" data-testid={`not-allowed-${b.brand_id}`}>Not allowed here</Badge>}
                    </td>
                    <td className="px-3 py-2 text-zinc-600">{b.held_by?.business_name || "—"}</td>
                    <td className="px-3 py-2 text-zinc-400">
                      {b.restricted && <span className="text-red-600 text-[10px] block" data-testid={`restriction-reason-${b.brand_id}`}>{b.restriction_reason}</span>}
                      {b.pending_request && <span className="text-amber-600 text-[10px]">Pending change request</span>}
                      {b.pending_applicant && <span className="text-blue-600 text-[10px]">Pending applicant: {b.pending_applicant.business_name}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
